import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, Calendar, CreditCard, CheckCircle, Clock, XCircle,
  FileText, Building, DollarSign, TrendingUp, AlertCircle
} from 'lucide-react';
import { clientApiService } from '../../services/clientApi';
import { Souscription, Paiement, Echeancier } from '../../types/api';
import Breadcrumb from '../../components/common/Breadcrumb';
import { useTexts } from '../../hooks/useTexts';

export default function SubscriptionDetailPage() {
  const { getText } = useTexts();
  const { id } = useParams<{ id: string }>();
  
  const [subscription, setSubscription] = useState<Souscription | null>(null);
  const [payments, setPayments] = useState<Paiement[]>([]);
  const [echeanciers, setEcheanciers] = useState<Echeancier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Charger les données de la souscription
  useEffect(() => {
    const loadSubscriptionData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Charger la souscription spécifique
        const souscriptionsResponse = await clientApiService.getMySouscriptions();
        const foundSubscription = souscriptionsResponse.results.find(s => s.id === parseInt(id || '0'));
        
        if (!foundSubscription) {
          setError('Souscription non trouvée');
          return;
        }
        
        setSubscription(foundSubscription);

        // Charger les paiements de cette souscription
        const paiementsResponse = await clientApiService.getMyPaiements({ souscription: id });
        setPayments(paiementsResponse.results);

        // Charger les échéances de cette souscription
        const echeanciersResponse = await clientApiService.getMyEcheanciers({ souscription: id });
        const echeanciersList = Array.isArray(echeanciersResponse) ? echeanciersResponse : echeanciersResponse.results;
        setEcheanciers(echeanciersList || []);

      } catch (err) {
        console.error('Erreur lors du chargement des données:', err);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadSubscriptionData();
    }
  }, [id]);

  // Calculs des statistiques
  const totalPaid = payments
    .filter(p => p.statut_paiement === 'VALIDE')
    .reduce((sum, p) => sum + Number(p.montant_total_paye || p.montant_paye), 0);


  const remainingAmount = subscription ? Number(subscription.prix_convenu_ht) - totalPaid : 0;
  const progressPercentage = subscription ? Math.round((totalPaid / Number(subscription.prix_convenu_ht)) * 100) : 0;

  // Échéances à venir et en retard
  const upcomingEcheances = (echeanciers || []).filter(e => 
    e.statut_echeance === 'EN_ATTENTE' && 
    new Date(e.date_echeance) > new Date()
  );

  const overdueEcheances = (echeanciers || []).filter(e => 
    e.statut_echeance === 'EN_RETARD'
  );

  const paidEcheances = (echeanciers || []).filter(e => 
    e.statut_echeance === 'PAYE'
  );

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'SUSPENDUE':
        return <Clock className="w-5 h-5 text-orange-500" />;
      case 'TERMINEE':
        return <CheckCircle className="w-5 h-5 text-blue-500" />;
      case 'ANNULEE':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'SUSPENDUE':
        return 'bg-orange-100 text-orange-800';
      case 'TERMINEE':
        return 'bg-blue-100 text-blue-800';
      case 'ANNULEE':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getEcheanceStatusIcon = (status: string) => {
    switch (status) {
      case 'PAYE':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'EN_ATTENTE':
        return <Clock className="w-4 h-4 text-orange-500" />;
      case 'EN_RETARD':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getEcheanceStatusColor = (status: string) => {
    switch (status) {
      case 'PAYE':
        return 'bg-green-100 text-green-800';
      case 'EN_ATTENTE':
        return 'bg-orange-100 text-orange-800';
      case 'EN_RETARD':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Chargement des détails...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !subscription) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-600 mr-2" />
              <span className="text-red-800">{error || getText('subscription.detail.not_found')}</span>
            </div>
            <div className="mt-4">
              <Link
                to="/dashboard"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {getText('subscription.detail.back_dashboard')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: 'Souscriptions', href: '/subscriptions' },
              { label: `Souscription #${subscription.id}`, current: true }
            ]}
          />
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {getText('subscription.detail.title')}
              </h1>
              <p className="text-gray-600 mt-2">
                Souscription #{subscription.id} - {subscription.reference_contrat || 'N/A'}
              </p>
            </div>
            <div className="flex items-center">
              {getStatusIcon(subscription.statut_souscription)}
              <span className={`ml-2 px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(subscription.statut_souscription)}`}>
                {subscription.statut_souscription}
              </span>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Prix Total</p>
                <p className="text-2xl font-bold text-gray-900">
                  {Number(subscription.prix_convenu_ht).toLocaleString('fr-FR')} FCFA
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Montant Payé</p>
                <p className="text-2xl font-bold text-gray-900">
                  {totalPaid.toLocaleString('fr-FR')} FCFA
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Reste à Payer</p>
                <p className="text-2xl font-bold text-gray-900">
                  {remainingAmount.toLocaleString('fr-FR')} FCFA
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Progression</p>
                <p className="text-2xl font-bold text-gray-900">{progressPercentage}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Progression du Paiement</h3>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(progressPercentage, 100)}%` }}
            />
          </div>
          <div className="flex justify-between text-sm text-gray-600 mt-2">
            <span>0 FCFA</span>
            <span>{Number(subscription.prix_convenu_ht).toLocaleString('fr-FR')} FCFA</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Informations Générales */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Informations Générales</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center">
                <Building className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Type de Maison</p>
                  <p className="font-medium text-gray-900">{subscription.type_maison_designation || 'N/A'}</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Date de Souscription</p>
                  <p className="font-medium text-gray-900">
                    {new Date(subscription.date_souscription).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center">
                <FileText className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Numéro de Contrat</p>
                  <p className="font-medium text-gray-900">{subscription.reference_contrat || 'N/A'}</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <DollarSign className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Mensualité de Base</p>
                  <p className="font-medium text-gray-900">
                    {Number(subscription.mensualite_convenue).toLocaleString('fr-FR')} FCFA
                  </p>
                </div>
              </div>
              
              <div className="flex items-center">
                <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Durée de Paiement</p>
                  <p className="font-medium text-gray-900">{subscription.duree_paiement_mois} mois</p>
                </div>
              </div>
            </div>
          </div>

          {/* Échéances */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Échéances</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{paidEcheances.length}</div>
                  <div className="text-sm text-gray-600">Payées</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">{upcomingEcheances.length}</div>
                  <div className="text-sm text-gray-600">À Venir</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{overdueEcheances.length}</div>
                  <div className="text-sm text-gray-600">En Retard</div>
                </div>
              </div>
              
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {(echeanciers || []).slice(0, 5).map((echeance) => (
                  <div key={echeance.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center">
                      {getEcheanceStatusIcon(echeance.statut_echeance)}
                      <div className="ml-3">
                        <p className="font-medium text-gray-900">
                          Échéance #{echeance.numero_echeance}
                        </p>
                        <p className="text-sm text-gray-600">
                          {new Date(echeance.date_echeance).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">
                        {Number(echeance.montant_prevu).toLocaleString('fr-FR')} FCFA
                      </p>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getEcheanceStatusColor(echeance.statut_echeance)}`}>
                        {echeance.statut_echeance}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              {(echeanciers || []).length > 5 && (
                <div className="mt-4 text-center">
                  <Link
                    to={`/echeanciers?subscription=${id}`}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Voir toutes les échéances
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Paiements Récents */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Paiements Récents</h3>
              <Link
                to={`/payments?subscription=${id}`}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Voir tous les paiements
              </Link>
            </div>
          </div>
          <div className="p-6">
            {payments.length > 0 ? (
              <div className="space-y-4">
                {payments.slice(0, 5).map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-3 ${
                        payment.statut_paiement === 'VALIDE' ? 'bg-green-500' :
                        payment.statut_paiement === 'EN_ATTENTE' ? 'bg-orange-500' :
                        'bg-red-500'
                      }`} />
                      <div>
                        <p className="font-medium text-gray-900">
                          {Number(payment.montant_total_paye || payment.montant_paye).toLocaleString('fr-FR')} FCFA
                        </p>
                        <p className="text-sm text-gray-600">
                          {new Date(payment.date_paiement).toLocaleDateString('fr-FR')} - {payment.mode_paiement}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        payment.statut_paiement === 'VALIDE' ? 'bg-green-100 text-green-800' :
                        payment.statut_paiement === 'EN_ATTENTE' ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {payment.statut_paiement}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Aucun paiement enregistré</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
